import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { BUBBLE_CLASSES } from './Tooltip';

/**
 * Replaces the browser's native `title=` popup, app-wide, with the same bubble
 * `Tooltip` renders (Bill, 2026-08-23: "ALL need my custom style").
 *
 * Mounted ONCE, at the app root. There is no per-site markup: it listens for
 * hover at the document, and on the way in it LIFTS the `title` attribute off
 * the element and puts it back on the way out. Lifting it is the whole trick -
 * the browser only shows its own popup after a delay of about a second, and by
 * then the attribute is gone, so the nasty default never appears.
 *
 * Why interception rather than wrapping 125 call sites in <Tooltip>:
 *
 * - a wrapper is an extra element in the flow, and at a third of those sites
 *   that changed the layout (absolutely-placed handles, truncating labels,
 *   flex children). Nothing here touches the tree.
 * - it covers every `title=` written from here on, including in code nobody
 *   thought to convert. A sweep only covers what existed on the day.
 * - the attribute is RESTORED on leave, so it remains the element's accessible
 *   name for a screen reader. A wrapper removes that name unless each site also
 *   gains an aria-label.
 *
 * <Tooltip> is still the right tool where a site wants control - a chosen side,
 * `fixed`, or JSX rather than a string. This is the floor, not a replacement.
 *
 * ⚠️ It cannot see react-icons' `title` PROP, which renders a <title> child
 * inside the SVG rather than an attribute on it. Those sites are wrapped in
 * <Tooltip> by hand; `grep -n 'title=' frontend/src` over the icon components
 * is how to find any that were missed.
 */

// Below the trigger by default, flipping above when there is no room. 8px of
// air either way, matching Tooltip's mt-2/mb-2.
const GAP = 8;
const ESTIMATED_HEIGHT = 34;

const NativeTooltips = () => {
    const [tip, setTip] = useState(null);

    useEffect(() => {
        // The element whose title we are currently holding, and the value. Held
        // outside state: restoring must not depend on a render having happened.
        let held = null;
        let heldText = '';

        const restore = () => {
            if (held) {
                held.setAttribute('title', heldText);
                held = null;
                heldText = '';
            }
        };

        const hide = () => {
            restore();
            setTip(null);
        };

        const onOver = (event) => {
            const target = event.target instanceof Element
                ? event.target.closest('[title]')
                : null;

            if (!target) {
                if (held) hide();
                return;
            }
            if (target === held) return;

            // Moving straight from one titled element to another.
            restore();

            const text = target.getAttribute('title');
            if (!text) return;

            held = target;
            heldText = text;
            target.removeAttribute('title');

            const rect = target.getBoundingClientRect();
            const below = rect.bottom + GAP + ESTIMATED_HEIGHT <= window.innerHeight;

            setTip({
                text,
                below,
                left: rect.left + rect.width / 2,
                top: below ? rect.bottom + GAP : undefined,
                bottom: below ? undefined : window.innerHeight - rect.top + GAP
            });
        };

        const onOut = (event) => {
            // Only when the pointer has actually left the held element, not on
            // every move between its children.
            if (!held) return;
            const to = event.relatedTarget;
            if (to instanceof Node && held.contains(to)) return;
            hide();
        };

        document.addEventListener('mouseover', onOver, true);
        document.addEventListener('mouseout', onOut, true);
        // A bubble anchored to a rect goes stale the moment anything moves, and
        // a click usually means the trigger is about to change or disappear.
        window.addEventListener('scroll', hide, true);
        window.addEventListener('resize', hide);
        document.addEventListener('mousedown', hide, true);

        return () => {
            document.removeEventListener('mouseover', onOver, true);
            document.removeEventListener('mouseout', onOut, true);
            window.removeEventListener('scroll', hide, true);
            window.removeEventListener('resize', hide);
            document.removeEventListener('mousedown', hide, true);
            // Unmounting must not leave an element stripped of its title.
            restore();
        };
    }, []);

    if (!tip) return null;

    return createPortal(
        <span
            role="tooltip"
            data-testid="native-tooltip"
            className={`fixed ${BUBBLE_CLASSES}`}
            style={{
                left: tip.left,
                top: tip.top,
                bottom: tip.bottom,
                transform: 'translateX(-50%)'
            }}
        >
            {tip.text}
            <span
                className={`absolute left-1/2 -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-transparent ${
                    tip.below
                        ? 'bottom-full border-b-4 border-b-gray-800'
                        : 'top-full border-t-4 border-t-gray-800'
                }`}
            />
        </span>,
        document.body
    );
};

export default NativeTooltips;
